import { useMutation, useQueryClient } from '@tanstack/react-query';
import { pb } from '@/core/api';
import { useToast } from '@/components/ui/toast';
import type { Tier } from './use-pricing-table';
import type { PricingRange } from '@/hooks/use-pricing-ranges';

// 19b.7: Mutation hooks for /api/pricing/ranges admin endpoints.
// Queries live in use-pricing-ranges.ts.

export type CreateRangePayload = {
  range_min: number;
  range_label: string;
  tiers: Tier[];
};

export type UpdateRangeArgs = {
  rangeId: string;
  range_label?: string;
  tiers: Tier[];
};

export type MoveRangeArgs = {
  rangeId: string;
  range_min: number;
};

function useInvalidatePricing(): () => void {
  const qc = useQueryClient();
  return () => {
    qc.invalidateQueries({ queryKey: ['pricing-ranges'] });
    qc.invalidateQueries({ queryKey: ['pricing-active'] });
    qc.invalidateQueries({ queryKey: ['pricing-history'] });
    qc.invalidateQueries({ queryKey: ['pricing-range-history'] });
  };
}

export function useCreateRange() {
  const invalidate = useInvalidatePricing();
  const { toast } = useToast();
  return useMutation({
    mutationFn: (payload: CreateRangePayload): Promise<PricingRange> =>
      pb.send('/api/pricing/ranges', { method: 'POST', body: payload }),
    onSuccess: () => {
      invalidate();
      toast('Rango creado', 'success');
    },
    onError: () => toast('Error al crear rango', 'error'),
  });
}

function postRange(path: string, body: unknown): Promise<PricingRange> {
  return pb.send(`/api/pricing/ranges${path}`, { method: 'POST', body });
}

export function useUpdateRange() {
  const invalidate = useInvalidatePricing();
  const { toast } = useToast();
  return useMutation({
    mutationFn: ({ rangeId, ...body }: UpdateRangeArgs) =>
      postRange(`/${rangeId}/update`, body),
    onSuccess: () => {
      invalidate();
      toast('Tiers actualizados', 'success');
    },
    onError: () => toast('Error al actualizar tiers', 'error'),
  });
}

export function useMoveRange() {
  const invalidate = useInvalidatePricing();
  const { toast } = useToast();
  return useMutation({
    mutationFn: ({ rangeId, ...body }: MoveRangeArgs) =>
      postRange(`/${rangeId}/move`, body),
    onSuccess: () => {
      invalidate();
      toast('Rango movido', 'success');
    },
    onError: () => toast('Error al mover rango', 'error'),
  });
}

export function useDeleteRange() {
  const invalidate = useInvalidatePricing();
  const { toast } = useToast();
  return useMutation({
    mutationFn: (rangeId: string) =>
      pb.send(`/api/pricing/ranges/${rangeId}`, { method: 'DELETE' }),
    onSuccess: () => {
      invalidate();
      toast('Rango eliminado', 'success');
    },
    onError: () => toast('Error al eliminar rango', 'error'),
  });
}
